import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import celeryJobService from '../../services/celeryJobService';

const JobHistory = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, failed, active

  useEffect(() => {
    loadJobs();
    
    // Refresh every 10 seconds if there are active jobs
    const interval = setInterval(() => {
      const hasActiveJobs = jobs.some(job => 
        ['PENDING', 'STARTED', 'PROGRESS', 'RETRY'].includes(job.status)
      );
      if (hasActiveJobs) {
        loadJobs();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [filter]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const statusFilter = filter === 'all' ? null : filter;
      const jobList = await celeryJobService.listRecentTasks(20, statusFilter);
      setJobs(jobList);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'FAILURE':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'PENDING':
      case 'STARTED':
        return <Clock className="w-5 h-5 text-gray-400" />;
      case 'PROGRESS':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'RETRY':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'SUCCESS':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'FAILURE':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'PENDING':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'STARTED':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'PROGRESS':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'RETRY':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDuration = (dateCreated, dateDone) => {
    if (!dateDone) return '-';
    
    const start = new Date(dateCreated);
    const end = new Date(dateDone);
    const durationMs = end - start;
    const durationSec = Math.floor(durationMs / 1000);
    
    if (durationSec < 60) {
      return `${durationSec}s`;
    } else if (durationSec < 3600) {
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      return `${minutes}m ${seconds}s`;
    } else {
      const hours = Math.floor(durationSec / 3600);
      const minutes = Math.floor((durationSec % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Sync Job History</h3>
          <button
            onClick={loadJobs}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="mt-4 flex space-x-2">
          {['all', 'completed', 'failed', 'active'].map((filterOption) => (
            <button
              key={filterOption}
              onClick={() => setFilter(filterOption)}
              className={`
                px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                ${filter === filterOption
                  ? 'bg-brand text-black'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Jobs List */}
      <div className="divide-y divide-gray-200">
        {loading && jobs.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No jobs found</p>
          </div>
        ) : (
          jobs.map((job) => (
            <div key={job.task_id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {/* Status Icon */}
                  <div className="mt-1">
                    {getStatusIcon(job.status)}
                  </div>

                  {/* Job Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={getStatusBadge(job.status)}>
                        {job.status}
                      </span>
                      {job.progress !== undefined && job.status === 'PROGRESS' && (
                        <span className="text-xs text-gray-500">
                          {job.progress}%
                        </span>
                      )}
                    </div>

                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {job.data?.job_type || 'Order Sync'}
                    </p>

                    <p className="text-xs text-gray-500 font-mono truncate">
                      Task ID: {job.task_id}
                    </p>

                    {/* Result Summary */}
                    {job.result && job.status === 'SUCCESS' && (
                      <div className="mt-2 text-xs text-gray-600 space-x-4">
                        <span>Checked: {job.result.orders_checked || 0}</span>
                        <span>Synced: {job.result.orders_synced || 0}</span>
                        <span>Updated: {job.result.orders_with_changes || 0}</span>
                        {job.result.orders_failed > 0 && (
                          <span className="text-red-600">
                            Failed: {job.result.orders_failed}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Progress Bar for Active Jobs */}
                    {job.status === 'PROGRESS' && job.progress !== undefined && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamp and Duration */}
                <div className="ml-4 text-right flex-shrink-0">
                  <p className="text-xs text-gray-500">
                    {job.date_created && format(new Date(job.date_created), 'MMM d, HH:mm')}
                  </p>
                  {job.date_done && (
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDuration(job.date_created, job.date_done)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default JobHistory;
